import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Wifi, WifiOff, Upload, CheckCircle, AlertCircle } from 'lucide-react';

const UploadProgress = ({ cameraUploader, screenUploader }) => {
  const [cameraProgress, setCameraProgress] = useState({ uploaded: 0, total: 0 });
  const [screenProgress, setScreenProgress] = useState({ uploaded: 0, total: 0 });
  const [isOnline, setIsOnline] = useState(true);
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    if (!cameraUploader || !screenUploader) return;

    // Setup progress callbacks
    cameraUploader.onProgress((progress) => {
      setCameraProgress(progress);
      setShowProgress(true);
    });

    screenUploader.onProgress((progress) => {
      setScreenProgress(progress);
      setShowProgress(true);
    });

    // Setup network status callbacks
    cameraUploader.onNetworkStatus((online) => {
      setIsOnline(online);
    });

  }, [cameraUploader, screenUploader]);

  if (!showProgress) return null;

  const cameraPercent = cameraProgress.total > 0 
    ? Math.round((cameraProgress.uploaded / cameraProgress.total) * 100) 
    : 0;
  
  const screenPercent = screenProgress.total > 0 
    ? Math.round((screenProgress.uploaded / screenProgress.total) * 100) 
    : 0;

  const allUploaded = 
    cameraProgress.uploaded === cameraProgress.total &&
    screenProgress.uploaded === screenProgress.total &&
    cameraProgress.total > 0 &&
    screenProgress.total > 0;

  return (
    <Card className="fixed bottom-4 right-4 w-80 shadow-lg z-50">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {allUploaded ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-sm">التسجيل مكتمل</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 text-blue-600 animate-pulse" />
                <span className="font-semibold text-sm">جاري الرفع...</span>
              </>
            )}
          </div>
          
          {/* Network Status */}
          <div className="flex items-center gap-1">
            {isOnline ? (
              <>
                <Wifi className="h-4 w-4 text-green-600" />
                <span className="text-xs text-green-600">متصل</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-red-600" />
                <span className="text-xs text-red-600">غير متصل</span>
              </>
            )}
          </div>
        </div>

        {/* Camera Progress */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">📹 الكاميرا</span>
            <span className="font-medium">
              {cameraProgress.uploaded} / {cameraProgress.total}
            </span>
          </div>
          <Progress value={cameraPercent} className="h-2" />
        </div>

        {/* Screen Progress */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">🖥️ الشاشة</span>
            <span className="font-medium">
              {screenProgress.uploaded} / {screenProgress.total}
            </span>
          </div>
          <Progress value={screenPercent} className="h-2" />
        </div>

        {/* Status Messages */}
        {!isOnline && (
          <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-800">
              الاتصال مفقود. سيتم استئناف الرفع عند عودة الاتصال.
            </p>
          </div>
        )}

        {allUploaded && (
          <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-800">
              تم رفع جميع التسجيلات بنجاح!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UploadProgress;
